import { useTranslations } from "@zapier/i18n";
import {
  IconButton,
  Key,
  Select,
  SelectItem,
  VisuallyHidden,
} from "@zapier/design-system";
import styles from "./FoldersTypeFilter.module.css";
import { useState, useCallback, useEffect, useRef } from "react";
import {
  useEmitFolderSearchInteractionEvent,
  FolderSearchInteractionOptions,
  useRouterParams,
} from "@zapier/shared";
import {
  useAccountActionPermissions,
  useFolderActionPermissions,
  useSupportedAssets,
} from "@zapier/client";
import { Icon } from "@zapier/zinnia-icons";

interface Props {
  totalResults?: number;
  isLoading?: boolean;
}

export function FoldersTypeFilter({ totalResults, isLoading }: Props = {}) {
  const t = useTranslations("folders-type-filter");

  const { folderType, setParams } = useRouterParams();
  const [selected, setSelected] = useState<string>("all");
  const [announcement, setAnnouncement] = useState("");
  // Only announce filtered result counts after the user changes the filter,
  // never on initial page load.
  const hasUserFilteredReference = useRef(false);
  // The filter we last announced for — used to dedupe so unrelated result-count
  // changes (search, folder add/remove, background refetch) don't re-announce.
  const lastAnnouncedReference = useRef<string | null>(null);
  // The Select's trigger receives focus when the filter is cleared, so keyboard
  // and screen-reader users aren't dropped to the top of the document.
  const selectWrapperReference = useRef<HTMLDivElement>(null);
  const { emit } = useEmitFolderSearchInteractionEvent();
  const { isSharingEnabled } = useAccountActionPermissions();
  const { canFilterOwners } = useFolderActionPermissions({});
  const { isMPFEnabled, isLoading: isLoadingSupportedAssets } =
    useSupportedAssets();

  // The active filter is the URL-derived value ("all" when the param is absent);
  // `totalResults` corresponds to it.
  const activeFilter = folderType || FolderSearchInteractionOptions.All;
  const isFiltered = selected !== FolderSearchInteractionOptions.All;

  const labelForFilter = useCallback(
    (value: string) => {
      switch (value) {
        case FolderSearchInteractionOptions.OwnedByMe: {
          return t("options.owned-by-me");
        }
        case FolderSearchInteractionOptions.Personal: {
          return t("options.personal");
        }
        case FolderSearchInteractionOptions.SharedWithMe: {
          return t("options.shared-with-me");
        }
        case FolderSearchInteractionOptions.Shared: {
          return t("options.shared");
        }
        default: {
          return t("options.all-folders");
        }
      }
    },
    [t],
  );

  useEffect(() => {
    setSelected(folderType || FolderSearchInteractionOptions.All);
  }, [folderType]);

  // Announce the filtered result count on user-initiated filter changes.
  // `setParams` navigates asynchronously, so `selected` (local) updates a render
  // before `folderType`/`totalResults` (URL-derived). Wait until they agree so we
  // announce the count that belongs to the active filter, and dedupe on the
  // filter itself so unrelated count changes don't re-announce.
  useEffect(() => {
    if (
      !hasUserFilteredReference.current ||
      isLoading ||
      selected !== activeFilter ||
      activeFilter === lastAnnouncedReference.current
    ) {
      return;
    }
    lastAnnouncedReference.current = activeFilter;
    setAnnouncement(
      t("announcement.showing-count", {
        count: totalResults ?? 0,
        filter: labelForFilter(activeFilter),
      }),
    );
  }, [activeFilter, selected, totalResults, isLoading, labelForFilter, t]);

  const focusSelectTrigger = useCallback(() => {
    const wrapper = selectWrapperReference.current;
    // The real Select renders a <button> trigger; fall back to a native
    // <select> so focus restore is exercised in tests too.
    const trigger =
      wrapper?.querySelector<HTMLElement>("button") ??
      wrapper?.querySelector<HTMLElement>("select");
    trigger?.focus();
  }, []);

  const valueChange = useCallback(
    (value: Key | null) => {
      hasUserFilteredReference.current = true;
      const omit: string[] = [];
      if (value === FolderSearchInteractionOptions.Personal) {
        if (!canFilterOwners) {
          // remove owner param if switched to "personal" and user can't filter owners
          omit.push("owner");
        }
        emit({ optionSelected: FolderSearchInteractionOptions.Personal });
      }

      if (value === FolderSearchInteractionOptions.Shared) {
        emit({
          optionSelected: FolderSearchInteractionOptions.Shared,
        });
      }

      if (value === FolderSearchInteractionOptions.All) {
        emit({ optionSelected: FolderSearchInteractionOptions.All });
      }

      if (value === FolderSearchInteractionOptions.OwnedByMe) {
        emit({ optionSelected: FolderSearchInteractionOptions.OwnedByMe });
      }

      if (value === FolderSearchInteractionOptions.SharedWithMe) {
        emit({ optionSelected: FolderSearchInteractionOptions.SharedWithMe });
      }

      setSelected((value as string) || FolderSearchInteractionOptions.All);
      setParams(
        {
          folderType: (value as string) || FolderSearchInteractionOptions.All,
          page: 1,
        },
        omit,
      );
    },
    [setParams, setSelected, canFilterOwners, emit],
  );

  return (
    <div className={styles.root}>
      {/* Polite SR announcement of the filtered result count. */}
      <VisuallyHidden>
        <div role="status" aria-live="polite" aria-atomic="true">
          {announcement}
        </div>
      </VisuallyHidden>
      <div className={styles.select} ref={selectWrapperReference}>
        <Select
          label={t("labels.filter-folders")}
          isLabelHidden={true}
          placeholder={t("labels.select")}
          onChange={valueChange}
          selectedKey={selected}
        >
          <SelectItem
            value={FolderSearchInteractionOptions.All}
            textValue={t("options.all-folders")}
          >
            {t("options.all-folders")}
          </SelectItem>
          {(() => {
            if (isLoadingSupportedAssets) {
              return;
            }

            const value = isMPFEnabled
              ? FolderSearchInteractionOptions.OwnedByMe
              : FolderSearchInteractionOptions.Personal;

            const textValue = labelForFilter(value);

            return (
              <SelectItem value={value} textValue={textValue}>
                {textValue}
              </SelectItem>
            );
          })()}
          {(() => {
            if (isLoadingSupportedAssets) {
              return;
            }

            const value = isMPFEnabled
              ? FolderSearchInteractionOptions.SharedWithMe
              : FolderSearchInteractionOptions.Shared;

            const textValue = labelForFilter(value);

            return (
              <SelectItem value={value} textValue={textValue}>
                <span className={styles.shared}>
                  {textValue}{" "}
                  {!isSharingEnabled && (
                    <Icon color="GrayWarm6" size="20" name="actionLock" />
                  )}
                </span>
              </SelectItem>
            );
          })()}
        </Select>
      </div>
      {/* Always rendered (disabled when inactive) so toggling it doesn't shift
          the toolbar layout; focus returns to the Select after a reset. */}
      <IconButton
        name="formX"
        variant="ghost"
        size="small"
        isDisabled={!isFiltered}
        onPress={() => {
          valueChange(FolderSearchInteractionOptions.All);
          focusSelectTrigger();
        }}
        aria-label={t("labels.clear-filter")}
      />
    </div>
  );
}
